import { ArrowUpward } from '@mui/icons-material';
import { Box, Checkbox, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { addCheckedItem, deleteCheckedItem, setSelectedItem, setSortSelectedItem } from 'app/actions/transport';
import { Autosizer, Protect, Scrollbar } from 'app/components';
import { TransportCircleIcon, TransportSquareIcon, TransportTriangleIcon } from 'app/icons';
import { checkedItemsSelector, selectedItemSelector, sortItemsSelector, sortSelectedItemSelector } from 'app/selectors/transport';
// import activity from 'app/services/activity';
import React, { createContext, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const TransportListContext = createContext();

const TransportList = () => {
  const dispatch = useDispatch();
  const selectedItem = useSelector(selectedItemSelector);
  const sortSelectedItem = useSelector(sortSelectedItemSelector);
  const checkedItems = useSelector(checkedItemsSelector);

  const sortItems = useSelector(sortItemsSelector);

  const handleItemClick = (value) => () => {
    // activity.logEntitySelect();
    dispatch(setSelectedItem(value));
  };

  const handleSortClick = (value) => () => {
    // activity.logUiInteraction();
    dispatch(setSortSelectedItem(value));
  };

  const handleCheckboxClick = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(checkedItems.indexOf(id) === -1 ? addCheckedItem(id) : deleteCheckedItem(id));
  };

  return (
    <Box
      component='div'
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-horizontal': {
          right: '10px',
        },
        '.simplebar-vertical': {
          top: '30px',
          bottom: '0',
        },
        '.MuiListItem-root': {
          width: '100%',
          userSelect: 'none',
          padding: theme.spacing(0),
          borderRadius: '0',
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <TransportListContext.Provider
              value={{
                sortItemClick: handleSortClick,
                sortSelectedItem,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    height={height}
                    itemSize={24}
                    overscanCount={10}
                    itemCount={sortItems.length}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    innerElementType={TransportListInnerElementType}
                    itemData={{
                      items: sortItems,
                      selectedItem,
                      onClick: handleItemClick,
                      onCheckboxClick: handleCheckboxClick,
                      checkedItems,
                    }}>
                    {TransportListItemRender}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </TransportListContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const TransportListInnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <TransportListContext.Consumer>
    {({ sortItemClick, sortSelectedItem }) => (
      <List
        ref={ref}
        disablePadding
        sx={{
          height: style.height,
          minHeight: '100%',
        }}
        {...rest}>
        <ListItem
          sx={(theme) => ({
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: style.height,
            minHeight: '100%',
            zIndex: '2',
            pointerEvents: 'none',
            padding: theme.spacing(0),
            '.MuiListItemText-root': {
              display: 'flex',
              justifyContent: 'space-between',
              height: '100%',
              position: 'relative',
              flex: 'none',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: '0',
                right: '0',
                height: '100%',
                bgcolor: theme.palette.divider,
                width: '1px',
                zIndex: '1',
                pointerEvents: 'none',
              },
              '&:last-of-type': {
                flex: '1',
                '&:before': {
                  display: 'none',
                },
              },
            },
          })}>
          <ListItemText style={{ width: '58px' }} />
          {config.transportColumns.map((column) => (
            <ListItemText key={column.name} style={{ width: column.width }} />
          ))}
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: '100%',
            display: 'flex',
            position: 'sticky !important',
            top: '0',
            zIndex: '1',
            '.MuiListItemButton-root': {
              display: 'flex',
              flex: 'none',
              padding: theme.spacing(0),
              position: 'relative',
              bgcolor: 'background.default',
              borderColor: theme.palette.divider,
              '&:first-of-type': {
                '.MuiListItemText-root': {
                  paddingLeft: '31px',
                },
              },
              '&:last-of-type': {
                flex: '1',
                '.MuiListItemText-root': {
                  paddingRight: theme.spacing(2),
                },
              },
              '.MuiTouchRipple-rippleVisible': {
                color: 'inherit',
              },
              '.MuiListItemText-root': {
                display: 'flex',
                cursor: 'pointer',
                margin: theme.spacing(0),
                padding: '6px 6px',
                alignItems: 'center',
                justifyContent: 'space-between',
                '.MuiTypography-root': {
                  display: 'flex',
                  '&:nth-of-type(1)': {
                    color: 'text.secondary',
                    marginRight: '4px',
                  },
                  '&:nth-of-type(2) svg': {
                    fontSize: '12px',
                    transition: 'all 300ms',
                    transform: 'rotateZ(0deg)',
                  },
                },
              },
            },
          })}>
          <ListItemButton disableTouchRipple style={{ minWidth: '58px', minHeight: '30px' }}>
            <ListItemText disableTypography />
          </ListItemButton>
          {config.transportColumns.map((column) => (
            <ListItemButton
              key={column.name}
              onClick={sortItemClick(column)}
              disableTouchRipple={!column.order}
              sx={(theme) => ({
                minWidth: column.width,
                '.MuiListItemText-root': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  '.MuiTypography-root': {
                    width: 'auto',
                    '&:nth-of-type(2)': {
                      opacity: '0.2',
                      transform: 'rotateZ(0)',
                      transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                      ...(sortSelectedItem.name === column.name && {
                        opacity: '1',
                        ...(sortSelectedItem.order === 'DESC' && {
                          transform: 'rotateZ(180deg)',
                        }),
                      }),
                    },
                  },
                },
              })}>
              <ListItemText disableTypography>
                <Typography>{column.label}</Typography>
                <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
              </ListItemText>
            </ListItemButton>
          ))}
        </ListItem>
        {children}
      </List>
    )}
  </TransportListContext.Consumer>
));

const TransportListItemRender = ({ data, index, style }) => {
  const { items, selectedItem, onClick, onCheckboxClick, checkedItems } = data;

  return (
    <ListItem
      key={index}
      disablePadding
      style={style}
      selected={items[index].id === selectedItem?.id}
      onClick={onClick(items[index])}
      sx={(theme) => ({
        width: '100%',
        marginTop: '30px',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            svg: {
              fontSize: '16px',
              marginRight: '3px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
          '.MuiCheckbox-root': {
            padding: theme.spacing(0),
            marginLeft: '-2px',
            svg: {
              fontSize: '18px',
            },
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      <ListItemText disableTypography style={{ width: '58px', justifyContent: 'center' }}>
        {
          {
            circle: <TransportCircleIcon color={items[index].color} />,
            triangle: <TransportTriangleIcon color={items[index].color} />,
            square: <TransportSquareIcon color={items[index].color} />,
            null: <TransportCircleIcon sx={{ opacity: 0 }} />,
          }[items[index].group_type]
        }
        <Protect
          permissions={['create', 'delete', 'update']}
          renderPermitted={() => (
            <React.Fragment>
              <Checkbox
                size='small'
                color='primary'
                checked={checkedItems.indexOf(items[index].id) !== -1}
                onClick={onCheckboxClick(items[index].id)}
                disableRipple
              />
            </React.Fragment>
          )}
          renderForbidden={() => <React.Fragment />}
        />
      </ListItemText>
      {config.transportColumns.map((column) => (
        <ListItemText key={column.name} style={{ width: column.width }}>
          <Typography>{column.format ? column.format(items[index][column.name]) : items[index][column.name]}</Typography>
        </ListItemText>
      ))}
    </ListItem>
  );
};

export default TransportList;
